import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export type InvoiceItem = { name: string; qty: number; price: number };
export type InvoiceData = {
  invoiceNo: string;
  date: string;
  customer: { name: string; phone?: string; email?: string; address?: string };
  items: InvoiceItem[];
  discount?: number;
  tax?: number;
  shipping?: number;
  notes?: string;
};

const fmt = (n: number) =>
  `BDT ${n.toLocaleString("en-BD", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const buildInvoicePdf = (data: InvoiceData) => {
  const {
    invoiceNo,
    date,
    customer,
    items,
    discount = 0,
    tax = 0,
    shipping = 0,
    notes = "Thank you for your purchase!",
  } = data;
  const subtotal = items.reduce((s, i) => s + i.qty * i.price, 0);
  const grandTotal = Math.max(0, subtotal - discount + tax + shipping);

  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const pageW = 210;
  const margin = 18;
  const accent: [number, number, number] = [234, 88, 12];

  doc.setFont("helvetica", "bold");
  doc.setFontSize(26);
  doc.setTextColor(...accent);
  doc.text("Rupshoili", margin, 22);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text("Honest products, honest prices.", margin, 28);
  doc.text("Dhaka, Bangladesh", margin, 32);
  doc.text("support@rupshoili.com", margin, 36);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.setTextColor(30);
  doc.text("INVOICE", pageW - margin, 22, { align: "right" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text(`Invoice #: ${invoiceNo}`, pageW - margin, 28, { align: "right" });
  doc.text(`Date: ${date}`, pageW - margin, 32, { align: "right" });

  doc.setDrawColor(...accent);
  doc.setLineWidth(0.6);
  doc.line(margin, 42, pageW - margin, 42);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(30);
  doc.text("BILL TO", margin, 50);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60);
  const lines = [
    customer.name || "—",
    customer.phone && `Phone: ${customer.phone}`,
    customer.email && `Email: ${customer.email}`,
    customer.address,
  ].filter(Boolean) as string[];
  lines.forEach((l, i) => doc.text(l, margin, 56 + i * 5));

  const tableStartY = 56 + lines.length * 5 + 8;

  autoTable(doc, {
    startY: tableStartY,
    head: [["#", "Product", "Qty", "Price", "Total"]],
    body: items.map((it, i) => [
      String(i + 1),
      it.name || "—",
      String(it.qty),
      fmt(it.price),
      fmt(it.qty * it.price),
    ]),
    theme: "grid",
    margin: { left: margin, right: margin },
    headStyles: { fillColor: accent, textColor: 255, fontStyle: "bold", fontSize: 10 },
    bodyStyles: { fontSize: 10, textColor: 40 },
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      2: { cellWidth: 18, halign: "center" },
      3: { cellWidth: 35, halign: "right" },
      4: { cellWidth: 35, halign: "right" },
    },
    styles: { lineColor: [220, 220, 220], lineWidth: 0.2, cellPadding: 2.5 },
  });

  const finalY = (doc as any).lastAutoTable.finalY + 8;
  const labelX = pageW - margin - 55;
  const valueX = pageW - margin;
  doc.setFontSize(10);
  doc.setTextColor(60);
  doc.setFont("helvetica", "normal");
  let rowY = finalY;
  doc.text("Subtotal", labelX, rowY);
  doc.text(fmt(subtotal), valueX, rowY, { align: "right" });
  rowY += 6;
  if (shipping) {
    doc.text("Shipping", labelX, rowY);
    doc.text(fmt(shipping), valueX, rowY, { align: "right" });
    rowY += 6;
  }
  doc.text("Discount", labelX, rowY);
  doc.text(`- ${fmt(discount)}`, valueX, rowY, { align: "right" });
  rowY += 6;
  doc.text("Tax", labelX, rowY);
  doc.text(fmt(tax), valueX, rowY, { align: "right" });
  rowY += 6;

  const gtY = rowY + 6;
  doc.setFillColor(...accent);
  doc.rect(labelX - 5, gtY - 5, pageW - margin - (labelX - 5), 10, "F");
  doc.setTextColor(255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.text("GRAND TOTAL", labelX, gtY + 1.5);
  doc.text(fmt(grandTotal), valueX, gtY + 1.5, { align: "right" });

  const footerY = 270;
  doc.setDrawColor(220);
  doc.setLineWidth(0.2);
  doc.line(margin, footerY, pageW - margin, footerY);
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(80);
  doc.text(notes, margin, footerY + 7);

  doc.setDrawColor(120);
  doc.line(pageW - margin - 50, footerY + 15, pageW - margin, footerY + 15);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text("Authorized Signature", pageW - margin - 25, footerY + 19, { align: "center" });

  return doc;
};

export const invoiceNoFromOrder = (order: { id: string; created_at: string }) => {
  const d = new Date(order.created_at);
  const y = d.getFullYear().toString().slice(-2);
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `RUP-${y}${m}${day}-${order.id.slice(0, 6).toUpperCase()}`;
};
